// Generated from Backend Media Library Controller
import {
  useSuspenseQuery,
  useQuery,
  useMutation,
  useQueryClient,
  UseSuspenseQueryOptions,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { useApiClient } from "../client";
import {
  MediaLibraryItemDto,
  MediaLibraryListPaginatedResponseDto,
  MediaLibraryRenameDto,
  MediaLibraryUploadFormData,
  MediaLibraryDownloadResponseDto,
  PaginationQueryDto,
  IdSearchFilterQueryDto,
  MediaLibraryCategoriesResponseDto,
  MediaLibraryReplaceFormData,
  MediaLibraryUsageResponseDto,
} from "../interfaces/media-library.interface";

// Query keys for media library-related queries
export const mediaLibraryKeys = {
  all: ["media-library"] as const,
  list: (pagination?: PaginationQueryDto, filter?: IdSearchFilterQueryDto) =>
    [...mediaLibraryKeys.all, "list", pagination, filter] as const,
  upload: () => [...mediaLibraryKeys.all, "upload"] as const,
  delete: () => [...mediaLibraryKeys.all, "delete"] as const,
  rename: () => [...mediaLibraryKeys.all, "rename"] as const,
  download: () => [...mediaLibraryKeys.all, "download"] as const,
  categories: () => [...mediaLibraryKeys.all, "categories"] as const,
  replace: () => [...mediaLibraryKeys.all, "replace"] as const,
  usage: (id: string) => [...mediaLibraryKeys.all, "usage", id] as const,
};

function appendCategoriesFilter(params: URLSearchParams, categories: IdSearchFilterQueryDto["categories"]) {
  if (!categories) return;

  if (categories.length === 0) {
    params.append("categories", "");
    return;
  }

  categories.forEach((cat) => params.append("categories", cat));
}

/**
 * Get all media items in the organization media library
 */
export function useMediaLibraryList(
  pagination?: PaginationQueryDto,
  filter?: IdSearchFilterQueryDto,
  options?: Omit<
    UseSuspenseQueryOptions<MediaLibraryListPaginatedResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: mediaLibraryKeys.list(pagination, filter),
    queryFn: () => {
      const params = new URLSearchParams();
      
      // Add pagination parameters
      if (pagination?.page !== undefined) params.append("page", pagination.page.toString());
      if (pagination?.limit !== undefined) params.append("limit", pagination.limit.toString());
      
      // Add filter parameters
      if (filter?.ids && filter.ids.length > 0) {
        filter.ids.forEach(id => params.append("ids", id));
      }
      if (filter?.textSearch) {
        params.append("textSearch", filter.textSearch);
      }
      appendCategoriesFilter(params, filter?.categories);
      // Add sorting params
      if (filter?.sortBy) {
        params.append("sortBy", filter.sortBy);
      }
      if (filter?.direction) {
        params.append("direction", filter.direction);
      }
      if (filter?.mimeType && filter.mimeType.length > 0) {
        filter.mimeType.forEach(type => params.append("mimeType", type));
      }

      const queryString = params.toString();
      const url = queryString 
        ? `/organizations/media-library?${queryString}`
        : `/organizations/media-library`;

      return apiRequest<MediaLibraryListPaginatedResponseDto>(url);
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Get all media items in the organization media library (Query version)
 */
export function useMediaLibraryListQuery(
  pagination?: PaginationQueryDto,
  filter?: IdSearchFilterQueryDto,
  options?: Omit<
    UseQueryOptions<MediaLibraryListPaginatedResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: mediaLibraryKeys.list(pagination, filter),
    queryFn: () => {
      const params = new URLSearchParams();
      
      // Add pagination parameters
      if (pagination?.page !== undefined) params.append("page", pagination.page.toString());
      if (pagination?.limit !== undefined) params.append("limit", pagination.limit.toString());
      
      // Add filter parameters
      if (filter?.ids && filter.ids.length > 0) {
        filter.ids.forEach(id => params.append("ids", id));
      }
      if (filter?.textSearch) {
        params.append("textSearch", filter.textSearch);
      }
      appendCategoriesFilter(params, filter?.categories);

      // Add sorting params
      if (filter?.sortBy) {
        params.append("sortBy", filter.sortBy);
      }
      if (filter?.direction) {
        params.append("direction", filter.direction);
      }
      if (filter?.mimeType && filter.mimeType.length > 0) {
        filter.mimeType.forEach(type => params.append("mimeType", type));
      }

      const queryString = params.toString();
      const url = queryString 
        ? `/organizations/media-library?${queryString}`
        : `/organizations/media-library`;

      return apiRequest<MediaLibraryListPaginatedResponseDto>(url);
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Upload Media - Max 5MB
 */
export function useMediaLibraryUpload(
  options?: Omit<
    UseMutationOptions<MediaLibraryItemDto, Error, MediaLibraryUploadFormData>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MediaLibraryUploadFormData) => {
      if (!data.file) {
        throw new Error("No file provided");
      }
      
      if (!(data.file instanceof File)) {
        throw new Error("Invalid file object");
      }
      
      const formData = new FormData();
      
      console.log('MediaLibraryUpload: Preparing upload', {
        fileName: data.file.name,
        fileType: data.file.type,
        fileSize: data.file.size,
        categories: data.categories
      });

      formData.append("file", data.file);
      if (data.categories && data.categories.length > 0) {
        data.categories.forEach(cat => formData.append("categories", cat));
      }

      return apiRequest<MediaLibraryItemDto>(`/organizations/media-library`, "POST", formData);
    },
    onSuccess: (data, variables, context) => {
      // Optimistisch aus dem Cache entfernen, um automatisches Refetchen zu verhindern
      queryClient.setQueriesData(
        { queryKey: mediaLibraryKeys.all },
        (oldData: any) => {
          if (!oldData || !oldData.docs) return oldData;
          return {
            ...oldData,
            docs: oldData.docs.filter((item: any) => item.mediaId !== variables),
            totalDocs: Math.max(0, (oldData.totalDocs || 0) - 1)
          };
        }
      );
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    ...options,
  });
}

/**
 * Replace Media - Max 5MB
 * Sends multipart/form-data with the new file to POST /organizations/media-library/replace/:id
 */
export function useMediaLibraryReplace(
  options?: Omit<
    UseMutationOptions<MediaLibraryItemDto, Error, MediaLibraryReplaceFormData>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: mediaLibraryKeys.replace(),
    mutationFn: ({ id, file }: MediaLibraryReplaceFormData) => {
      if (!id) throw new Error("Missing media id");
      if (!(file instanceof File)) throw new Error("Invalid file object");

      const formData = new FormData();
      formData.append("file", file);

      return apiRequest<MediaLibraryItemDto>(`/organizations/media-library/replace/${encodeURIComponent(id)}`, "POST", formData);
    },
    onSuccess: () => {
      // Refresh all media lists to show updated item
      queryClient.invalidateQueries({ queryKey: mediaLibraryKeys.all });
      // Call user-provided onSuccess if present
      options?.onSuccess?.(undefined as any, undefined as any, undefined as any);
    },
    ...options,
  });
}

/**
 * Delete Media Item
 */
export function useMediaLibraryDelete(
  options?: Omit<
    UseMutationOptions<void, Error, string>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      const params = new URLSearchParams();
      params.append("id", id);

      return apiRequest<void>(`/organizations/media-library/delete?${params.toString()}`, "POST");
    },
    onSuccess: (data, variables, context) => {
      // Optimistisch aus dem Cache entfernen, um automatisches Refetchen zu verhindern
      queryClient.setQueriesData(
        { queryKey: mediaLibraryKeys.all },
        (oldData: any) => {
          if (!oldData || !oldData.docs) return oldData;
          return {
            ...oldData,
            docs: oldData.docs.filter((item: any) => item.mediaId !== variables),
            totalDocs: Math.max(0, (oldData.totalDocs || 0) - 1)
          };
        }
      );
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    ...options,
  });
}

/**
 * Bulk Delete Media Items
 */
export function useMediaLibraryBulkDelete(
  options?: Omit<
    UseMutationOptions<void, Error, { ids: string[] }>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { ids: string[] }) => {
      return apiRequest<void>(`/organizations/media-library/bulk-delete`, "POST", data);
    },
    onSuccess: (data, variables, context) => {
      // Optimistisch aus dem Cache entfernen, um automatisches Refetchen zu verhindern
      queryClient.setQueriesData(
        { queryKey: mediaLibraryKeys.all },
        (oldData: any) => {
          if (!oldData || !oldData.docs) return oldData;
          return {
            ...oldData,
            docs: oldData.docs.filter((item: any) => !variables.ids.includes(item.mediaId)),
            totalDocs: Math.max(0, (oldData.totalDocs || 0) - variables.ids.length)
          };
        }
      );
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    ...options,
  });
}

/**
 * Rename Media Item
 */
export function useMediaLibraryRename(
  options?: Omit<
    UseMutationOptions<MediaLibraryItemDto, Error, MediaLibraryRenameDto>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MediaLibraryRenameDto) => {
      return apiRequest<MediaLibraryItemDto>(`/organizations/media-library/rename`, "POST", data);
    },
    onSuccess: () => {
      // Invalidate all media library queries to refresh the list
      queryClient.invalidateQueries({
        queryKey: mediaLibraryKeys.all,
      });
    },
    ...options,
  });
}

/**
 * Download Media Item
 */
export function useMediaLibraryDownload(
  options?: Omit<
    UseMutationOptions<void, Error, string>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useMutation({
    mutationFn: async (mediaId: string) => {      
      // Get the download URL from the backend
      const response = await apiRequest<MediaLibraryDownloadResponseDto>(
        `/organizations/media-library/download?id=${encodeURIComponent(mediaId)}`, 
        "GET"
      );
      
      // Trigger browser download using the signed URL
      const link = document.createElement('a');
      link.href = response.downloadUrl;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    ...options,
  });
}

/**
 * Fetch distinct media categories (GET /organizations/media-library/categories)
 */
export function useMediaLibraryGetCategories(
  options?: Omit<UseQueryOptions<MediaLibraryCategoriesResponseDto>, "queryKey" | "queryFn">
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: mediaLibraryKeys.categories(),
    enabled: options?.enabled ?? false,
    queryFn: async (): Promise<MediaLibraryCategoriesResponseDto> => {
      return apiRequest<MediaLibraryCategoriesResponseDto>(`/organizations/media-library/categories`);
    },
    ...options,
  });
}

export function useMediaLibraryGetUsage(
  mediaId: string,
  options?: Omit<UseQueryOptions<MediaLibraryUsageResponseDto>, "queryKey" | "queryFn">
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: mediaLibraryKeys.usage(mediaId),
    enabled: options?.enabled ?? false,
    queryFn: async (): Promise<MediaLibraryUsageResponseDto> => {
      return apiRequest<MediaLibraryUsageResponseDto>(`/organizations/media-library/${encodeURIComponent(mediaId)}/usage`);
    },
    ...options,
  });
}
